import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Button,
  Chip,
  Paper,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { enrollInCourse, setCurrentCourse } from '../store/progressSlice';

const CourseDashboard: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const course = useAppSelector(state => (courseId ? state.courses[courseId] : undefined));
  const progress = useAppSelector(state =>
    courseId ? state.progress.courseProgress[courseId] : undefined
  );
  const isEnrolled = useAppSelector(state =>
    courseId ? state.progress.enrolledCourses.includes(courseId) : false
  );

  useEffect(() => {
    if (courseId && !isEnrolled) {
      dispatch(enrollInCourse(courseId));
    } else if (courseId) {
      dispatch(setCurrentCourse(courseId));
    }
  }, [courseId, isEnrolled, dispatch]);

  if (!course || !courseId) {
    return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Typography variant='h4'>Course not found</Typography>
      </Container>
    );
  }

  const completedModules = progress?.completedModules.length || 0;
  const totalModules = course.modules.length;
  const progressPercentage = totalModules > 0 ? (completedModules / totalModules) * 100 : 0;

  const handleModuleClick = (moduleId: string) => {
    navigate(`/courses/${courseId}/modules/${moduleId}`);
  };

  const handlePlaygroundClick = () => {
    navigate(`/courses/${courseId}/playground`);
  };

  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant='h3' component='h1' gutterBottom>
          {course.title}
        </Typography>

        <Typography variant='body1' color='text.secondary' paragraph>
          {course.description}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <Chip label={course.difficulty} color='primary' />
          <Chip label={`${course.estimatedHours} hours`} variant='outlined' />
          <Chip label={course.category} variant='outlined' />
        </Box>

        {progress && (
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant='h6' gutterBottom>
              Your Progress
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress variant='determinate' value={progressPercentage} />
              </Box>
              <Box sx={{ minWidth: 35 }}>
                <Typography variant='body2' color='text.secondary'>
                  {Math.round(progressPercentage)}%
                </Typography>
              </Box>
            </Box>
            <Typography variant='body2' color='text.secondary'>
              {completedModules} of {totalModules} modules completed
            </Typography>
          </Paper>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Typography variant='h5' gutterBottom>
            Course Modules
          </Typography>

          <Grid container spacing={2}>
            {course.modules.map((module, index) => {
              const isCompleted = progress?.completedModules.includes(module.id) || false;
              const isCurrent = progress?.currentModule === module.id;
              const isUnderDevelopment = module.isUnderDevelopment || false;

              return (
                <Grid size={{ xs: 12 }} key={module.id}>
                  <Card
                    sx={{
                      cursor: isUnderDevelopment ? 'not-allowed' : 'pointer',
                      border: isCurrent ? 2 : 1,
                      borderColor: isCurrent ? 'primary.main' : 'divider',
                      '&:hover': { boxShadow: isUnderDevelopment ? 0 : 2 },
                      opacity: isUnderDevelopment ? 0.6 : 1,
                      backgroundColor: isUnderDevelopment ? 'grey.50' : 'background.paper',
                    }}
                    onClick={() => !isUnderDevelopment && handleModuleClick(module.id)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography
                          variant='h6'
                          sx={{
                            flexGrow: 1,
                            color: isUnderDevelopment ? 'text.disabled' : 'text.primary',
                          }}
                        >
                          Module {index + 1}: {module.title}
                        </Typography>
                        {isUnderDevelopment && (
                          <Chip label='Under Development' color='warning' size='small' />
                        )}
                        {isCompleted && !isUnderDevelopment && (
                          <Chip label='Completed' color='success' size='small' />
                        )}
                        {isCurrent && !isCompleted && !isUnderDevelopment && (
                          <Chip label='Current' color='primary' size='small' />
                        )}
                      </Box>

                      <Typography
                        variant='body2'
                        color={isUnderDevelopment ? 'text.disabled' : 'text.secondary'}
                        paragraph
                      >
                        {module.description}
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {module.duration && (
                          <Chip
                            label={module.duration}
                            size='small'
                            variant='outlined'
                            sx={{
                              opacity: isUnderDevelopment ? 0.5 : 1,
                              color: isUnderDevelopment ? 'text.disabled' : undefined,
                            }}
                          />
                        )}
                        <Chip
                          label={`${module.exercises.length} exercises`}
                          size='small'
                          variant='outlined'
                          sx={{
                            opacity: isUnderDevelopment ? 0.5 : 1,
                            color: isUnderDevelopment ? 'text.disabled' : undefined,
                          }}
                        />
                        {module.quiz && (
                          <Chip
                            label='Quiz'
                            size='small'
                            variant='outlined'
                            sx={{
                              opacity: isUnderDevelopment ? 0.5 : 1,
                              color: isUnderDevelopment ? 'text.disabled' : undefined,
                            }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant='h5' gutterBottom>
            Course Tools
          </Typography>

          {course.tools?.playground && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Interactive Playground
                </Typography>
                <Typography variant='body2' color='text.secondary' paragraph>
                  Practice what you&apos;ve learned in an interactive environment.
                </Typography>
                <Button variant='contained' onClick={handlePlaygroundClick} fullWidth>
                  Open Playground
                </Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Course Stats
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant='body2'>
                  <strong>Modules:</strong> {course.modules.length}
                </Typography>
                <Typography variant='body2'>
                  <strong>Exercises:</strong>{' '}
                  {course.modules.reduce((acc, mod) => acc + mod.exercises.length, 0)}
                </Typography>
                <Typography variant='body2'>
                  <strong>Quizzes:</strong> {course.modules.filter(mod => mod.quiz).length}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CourseDashboard;
